// © 2026 Camp Promethean, LLC. All Rights Reserved.
// Phase 13 · RB-2 — is the current user the Promethean-blessed admin of a facility?
// Blessing is granted only through the platform-admin rail (facility_admin_grants);
// this hook just reads the result. Facility-role 'owner'/'admin' does NOT imply it.

import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";

interface UseIsBlessedAdminResult {
  isBlessedAdmin: boolean;
  loading: boolean;
  error: Error | null;
}

export function useIsBlessedAdmin(facilityId: string | null | undefined): UseIsBlessedAdminResult {
  const { profile, loading: authLoading } = useAuth();
  const [isBlessedAdmin, setIsBlessedAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function checkBlessing() {
      if (authLoading) return;
      if (!profile || !facilityId) {
        setIsBlessedAdmin(false);
        setLoading(false);
        return;
      }
      try {
        // facility_admin_grants is not in the generated types until the next
        // `supabase gen types` run — cast, don't fork the client.
        const { data, error: qError } = await (supabase as any)
          .from("facility_admin_grants")
          .select("id")
          .eq("facility_id", facilityId)
          .eq("user_id", profile.id)
          .is("revoked_at", null)
          .limit(1);
        if (cancelled) return;
        if (qError) throw qError;
        setIsBlessedAdmin((data ?? []).length > 0);
        setError(null);
      } catch (err) {
        if (cancelled) return;
        console.error("Error checking blessed-admin status:", err);
        setError(err instanceof Error ? err : new Error("Unknown error checking blessed-admin status"));
        setIsBlessedAdmin(false);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    checkBlessing();
    return () => {
      cancelled = true;
    };
  }, [profile, facilityId, authLoading]);

  return { isBlessedAdmin, loading, error };
}

export default useIsBlessedAdmin;
